//  Copyright 2019-2021
//  Myriota Pty Ltd
//  Myriota Confidential
/**
 * Colours being used from 2019
 */
export const Colours = {
    MYRIOTA_BLUE: "#0077C8",
    MYRIOTA_GREY: "#EEEEEE",
    WHITE: "#FFFFFF",
    ERROR_RED: "#D0011B",
    PRIMARY_TEXT: "#231F20",
    SECONDARY_TEXT: "#817A73",
    FEATURED_TEXT: "#0077C8",
    LINE: "#EEEEEE",
    MYRIOTA_LIGHT_BLUE: "#95D4E9",
    DARK_LINE: "#C7C0B8",
    PRIMARY_HOVER_STATE: "#1A91E2",
    DESTRUCTIVE_HOVER_STATE: "#FA0422",
    // parallax design 2020
    BLACK: "#000000",
    LIGHT_GREY_BACKGROUND: "#FAF9F8",
    GREY_BACKGROUND: "#F2F0EE",
    HOVER: "#CBC4BC",
    LIGHT_BLUE: "#E5F1F9",
    BUTTON_HOVER: "#80BBE3",
    SEARCH_BUTTON: "#CBC4BC",
    SEARCH_BUTTON_HOVER: "#E5E1DD",
    SEPARATION_LINE: "#8C8279",
    MYRIOTA_RED: "#E00000",
    MYRIOTA_RED_HOVER: "#EA7D43",
    // format {colour}_{saturation percentage}
    BLUE_100: "#0077C8",
    BLUE_75: "#4099D6",
    BLUE_50: "#80BBE3",
    BLUE_25: "#BFDDF1",
    BLUE_10: "#E5F1F9",
    BLACK_100: "#2C2A29",
    BLACK_75: "#555554",
    BLACK_50: "#8E8E8D",
    BLACK_10: "#E8E8E8",
    BLACK_25: "#C6C6C6",
    RED_100: "#E35205",
    RED_75: "#EA7D43",
    RED_50: "#F1A882",
    RED_25: "#F8D4C0",
    RED_10: "#FCEEE6",
    GREEN_100: "#B5BD00",
    GREEN_75: "#C8CE40",
    GREEN_50: "#DADE7F",
    GREEN_25: "#ECEEBF",
    GREEN_10: "#F7F8E5",
    GREY_100: "#8C8279",
    GREY_75: "#A29A93",
    GREY_50: "#C1BBB7",
    GREY_25: "#E0DDDB",
    LIGHT_GREY_100: "#CBC4BC",
    LIGHT_GREY_75: "#D8D3CD",
    LIGHT_GREY_50: "#E5E1DD",
    LIGHT_GREY_25: "#F2F0EE",
    LIGHT_GREY_10: "#FAF9F8",
    AQUA_100: "#00AEC7",
    ORANGE_100: "#F69B41",
    ORANGE_75: "#EFA75F",
    ORANGE_50: "#EDB57D",
    ORANGE_25: "#F6C484",
    ORANGE_10: "#F7DFC0",
};

export const MediaQueries = {
    /**
     * Return media query '@media (max-width)' string for jss
     * @param width Target width
     * @param style Style to be applied
     */
    screenAndMaxWidth: (width: number) => {
        return "@media (max-width: " + width + "px)";
    },
};

// Any string looks like {string}@{string}.{string}
// no space is allowed
export const EMAIL_REGEX = /^\S+@\S+\.\S+/;

// Any string looks like digits and "-" character
// no space is allowed
export const POSTCODE_REGEX = /^[\d-]+$/;

/**
 * Common theme for Material UI. Must be used by all applications that use material ui
 */
export const MATERIAL_UI_THEME = {
    breakpoints: {
        values: {
            xl: 1920,
            lg: 1280,
            md: 960,
            sm: 640,
            xs: 0,
        },
    },
    palette: {
        primary: {
            main: Colours.BLUE_100,
        },
        secondary: {
            main: Colours.WHITE,
        },
    },
    typography: {
        fontFamily: "GTWalsheimRegular, Arial",
        fontSize: 14,
        h5: {
            fontFamily: "GTWalsheimBold, Arial",
            fontWeight: "bold", // For backup font Arial
        },
        // temporary work around for auth forms
        button: {
            fontSize: 14,
        },
        body1: {
            lineHeight: "unset",
        },
        body2: {
            lineHeight: "unset",
        },
    },
    components: {
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    height: "19px",
                    width: "19px",
                    marginLeft: "-13px",
                    color: Colours.PRIMARY_TEXT,
                    "&:hover": {
                        backgroundColor: "unset",
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    "&:hover": {
                        backgroundColor: "unset",
                    },
                    color: Colours.BLACK_100,
                    padding: "5px",
                },
            },
        },
        MuiTouchRipple: {
            styleOverrides: {
                root: {
                    // hide touch ripple effect
                    color: "transparent",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 24,
                    maxWidth: 192,
                    height: 48,
                    flex: 1,
                    textTransform: "none",
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: Colours.BLACK_100,
                    opacity: "0 !important" as any,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    color: Colours.BLACK_50,
                },
                shrink: {
                    fontSize: 11,
                    color: Colours.BLACK_50,
                    transform: "translate(0, 1.5px)",
                },
            },
        },
        // input value
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    color: Colours.PRIMARY_TEXT,
                    height: 36,
                    marginTop: "12px !important",
                    borderRadius: "18px !important",
                },
                input: {
                    paddingTop: 0,
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                // change colour of underline of input field on focus
                underline: {
                    "&:after": {
                        borderBottomColor: Colours.BLACK_50,
                    },
                    "&:before": {
                        transition: "border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                        borderBottom: `1px solid ${Colours.BLACK_50}`,
                    },
                    "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: Colours.BLACK_50,
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    height: 72,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                // change the colour of label when input field is focused
                root: {
                    "&.Mui-focused": {
                        color: Colours.BLACK_50,
                    },
                    color: Colours.BLACK_50,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: Colours.RED_100,
                    fontSize: 11,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                list: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: "8px 0px 8px 15px",
                    color: Colours.BLACK_100,
                    fontSize: 14,
                    "&.Mui-selected": {
                        backgroundColor: `${Colours.BLUE_100} !important`,
                        color: Colours.WHITE,
                    },
                    "&:hover": {
                        backgroundColor: Colours.LIGHT_GREY_25,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    fontSize: 13,
                    "& fieldset": {
                        borderColor: Colours.BLACK_10,
                    },
                    "&:hover fieldset": {
                        borderColor: `${Colours.BLACK_10} !important`,
                    },
                    "&.Mui-focused fieldset": {
                        border: `1px solid ${Colours.BLACK_10} !important`,
                    },
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    "&:not(.Mui-selected)": {
                        border: "unset",
                    },
                    color: Colours.BLACK_100,
                    "&.Mui-selected": {
                        backgroundColor: `${Colours.BLUE_100} !important`,
                        color: Colours.WHITE,
                    },
                    "&:hover": {
                        backgroundColor: Colours.BLUE_50,
                    },
                },
                today: {
                    color: Colours.BLUE_100,
                },
            },
        },
    },
};
